import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('teacher');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, isAuthenticated, user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    
    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            if (user?.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/teacher');
            }
        }
    }, [isAuthenticated, authLoading, user, navigate]);

    const toggleLanguage = (lang) => {
        i18n.changeLanguage(lang);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(username, password, role);

        if (result.success) {
            if (result.user.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/teacher');
            }
        } else {
            const msg = result.message;
            const errorKey = `login.errors.${msg}`;
            const translatedText = t(errorKey);
            setError(translatedText !== errorKey ? translatedText : msg);
        }

        setLoading(false);
    };

    return (
        <div className="login-page">
            {/* Language Switcher */}
            <div className="login-lang-switcher">
                <button
                    className={`lang-btn ${i18n.language === 'ar' ? 'active' : ''}`}
                    onClick={() => toggleLanguage('ar')}
                >
                    العربية
                </button>
                <button
                    className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
                    onClick={() => toggleLanguage('en')}
                >
                    English
                </button>
            </div>

            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <div className="logo-banner">
                            <img src="/assets/img/logo.png" alt="UOM Logo" className="uom-logo" />
                        </div>
                        <div className="login-header-content">
                            <div className="login-divider"></div>
                            <p className="login-app-name">{t('login.subtitle')}</p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        {error && (
                            <div className="alert alert-danger">
                                <span className="material-icons" style={{fontSize: '20px'}}>error_outline</span>
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="form-group">
                            <label className="label">{t('login.username')}</label>
                            <input
                                type="text"
                                className="input"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder={t('login.usernamePlaceholder')}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label className="label">{t('login.password')}</label>
                            <div className="password-input-group">
                                <div className="password-input-group">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="input"
                                        placeholder="••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-icon"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <span className="material-icons">
                                            {showPassword ? 'visibility_off' : 'visibility'}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="role-selector">
                            <label className={`role-card ${role === 'admin' ? 'active' : ''}`}>
                                <input 
                                    type="radio" 
                                    name="role" 
                                    value="admin" 
                                    checked={role === 'admin'} 
                                    onChange={(e) => setRole(e.target.value)} 
                                    className="hidden-radio"
                                />
                                <span className="material-icons">admin_panel_settings</span>
                                <span>{t('login.forAdmins')}</span>
                            </label>
                            
                            <label className={`role-card ${role === 'teacher' ? 'active' : ''}`}>
                                <input 
                                    type="radio" 
                                    name="role" 
                                    value="teacher" 
                                    checked={role === 'teacher'} 
                                    onChange={(e) => setRole(e.target.value)} 
                                    className="hidden-radio"
                                />
                                <span className="material-icons">school</span>
                                <span>{t('login.forTeachers')}</span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-secondary"
                            disabled={loading}
                        >
                            {loading ? <div className="spinner"></div> : t('login.submit')}
                        </button>

                        <div className="login-copyright-inside">
                            {t('login.copyright')}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;
